<template>
  <div class="dashboard" :class="{ 'achievements-unlocked': hasNewAchievement }">
    
    <!-- Animation de badge débloqué -->
    <div v-if="showBadgeUnlockAnimation" class="badge-unlock-overlay">
      <!-- Particules de célébration -->
      <div class="celebration-particles">
        <div v-for="n in 20" :key="n" class="particle" :style="getParticleStyle(n)"></div>
      </div>
      
      <div class="badge-unlock-animation" :class="{ 'special-celebration': newlyUnlockedBadge && newlyUnlockedBadge.id === 9 }">
        <!-- Badge avec effet de brillance -->
        <div class="badge-container" v-if="newlyUnlockedBadge">
          <div class="badge-shine"></div>
          <div class="badge-icon">{{ newlyUnlockedBadge.icon }}</div>
          <div class="badge-glow"></div>
        </div>
        
        <!-- Texte avec animation séquentielle -->
        <div class="text-container">
          <h2 v-if="newlyUnlockedBadge && newlyUnlockedBadge.id === 9" class="animated-text" :style="{animationDelay: '0.3s'}">🎉 Félicitations ! 🎉</h2>
          <h2 v-else class="animated-text" :style="{animationDelay: '0.3s'}">Badge débloqué !</h2>
          <h3 v-if="newlyUnlockedBadge" class="animated-text" :style="{animationDelay: '0.6s'}">{{ newlyUnlockedBadge.title }}</h3>
          <p v-if="newlyUnlockedBadge && newlyUnlockedBadge.id === 9" class="animated-text" :style="{animationDelay: '0.9s'}">
            Bravo ! Tu as terminé tous les jeux disponibles ! Tu es maintenant prêt à créer ton CV professionnel. 🌟
          </p>
          <p v-else-if="newlyUnlockedBadge" class="animated-text" :style="{animationDelay: '0.9s'}">{{ newlyUnlockedBadge.description }}</p>
        </div>
        
        <button @click="closeBadgeAnimation" class="close-animation-btn animated-btn" :class="{ 'celebration-btn': newlyUnlockedBadge && newlyUnlockedBadge.id === 9 }" :style="{animationDelay: '1.2s'}">
          Continuer
        </button>
      </div>
    </div>
    <!-- Guide Avatar pour guider l'utilisateur -->
    <guide-avatar
      v-if="!showRewardsModal"
      guide-name="Flamou"
      :forced-message="guideMessage"
      :forced-options="guideOptions"
      :force-show-message="guideForceShow"
      :context="guideContext"
      :auto-show-delay="0"
      :custom-position="guidePosition"
      :profile-tour-completed="profileTutorialCompleted"
      :dashboard-visited="!isFirstVisit"
      class="guide-top-left"
      @option-selected="handleGuideOptionSelected"
      @guide-opened="prepareGuideForRediscovery"
      @guide-dismissed="resetGuideState"
      @restart-dashboard-tour="restartDashboardTour"
    />

    <space-background v-if="animationsEnabled" :theme="currentTheme" />
    <static-backgrounds v-else :theme="currentTheme" />
    <!-- La structure principale -->
    <div class="dashboard-container">
      <!-- Avatar central avec cercle de progression amélioré -->
      <div
        class="avatar-container"
        @click="interactWithAvatar"
        :class="{ 'avatar-pulse': avatarAnimating }"
        role="button"
        tabindex="0"
        @keydown.enter="interactWithAvatar"
        aria-label="Accéder au profil"
      >
        <div
          v-if="highlightAvatar && isFirstVisit && !profileTutorialCompleted"
          class="avatar-highlight-effect"
          @click="interactWithAvatar"
        >
          <div class="pulse-ring"></div>
          <div class="hint-arrow">
            <span class="arrow">↓</span>
            <span class="hint-text">Clique ici!</span>
          </div>
        </div>
        <div class="progress-ring-container">
          <svg class="progress-ring" viewBox="0 0 260 260" preserveAspectRatio="xMidYMid meet">
            <!-- Background glow effect -->
            <filter id="glow">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>

            <!-- Background blur circle -->
            <circle
              class="progress-ring-blur"
              fill="transparent"
              stroke-width="15"
              r="120"
              cx="130"
              cy="130"
              stroke="url(#blurGradient)"
              filter="url(#glow)"
            />

            <!-- Background circle -->
            <circle
              class="progress-ring-circle-bg"
              stroke="#2a2a3a"
              fill="transparent"
              stroke-width="12"
              r="120"
              cx="130"
              cy="130"
            />

            <!-- Progress circle - Now with animation! -->
            <circle
              class="progress-ring-circle"
              stroke="url(#progressGradient)"
              stroke-linecap="round"
              fill="transparent"
              stroke-width="12"
              r="120"
              cx="130"
              cy="130"
              :style="{ strokeDashoffset: calculateProgressOffset() }"
            />

            <defs>
              <linearGradient
                id="progressGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stop-color="#4FC3F7">
                  <animate
                    attributeName="stop-color"
                    values="#4FC3F7;#7C4DFF;#FF4081;#4FC3F7"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="50%" stop-color="#7C4DFF">
                  <animate
                    attributeName="stop-color"
                    values="#7C4DFF;#FF4081;#4FC3F7;#7C4DFF"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stop-color="#FF4081">
                  <animate
                    attributeName="stop-color"
                    values="#FF4081;#4FC3F7;#7C4DFF;#FF4081"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>

              <linearGradient
                id="blurGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stop-color="rgba(79, 195, 247, 0.3)">
                  <animate
                    attributeName="stop-color"
                    values="rgba(79, 195, 247, 0.3);rgba(124, 77, 255, 0.3);rgba(255, 64, 129, 0.3);rgba(79, 195, 247, 0.3)"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stop-color="rgba(255, 64, 129, 0.3)">
                  <animate
                    attributeName="stop-color"
                    values="rgba(255, 64, 129, 0.3);rgba(79, 195, 247, 0.3);rgba(124, 77, 255, 0.3);rgba(255, 64, 129, 0.3)"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div class="avatar-image-container" role="button" tabindex="0">
          <AvatarDisplay 
            size="large" 
            custom-class="avatar-image"
            alt-text="Avatar utilisateur"
            picture-type="avatar"
          />
          <div class="level-badge">Niveau {{ calculateLevel() }}</div>
          <div class="avatar-glow" :class="{ pulse: avatarAnimating }"></div>
        </div>
      </div>

      <div class="current-level-badge" :class="{ 'badge-evolution-glow': badgeNeedsEvolution }">
        <!-- Effet de lueur d'évolution -->
        <div v-if="badgeNeedsEvolution" class="evolution-glow-ring"></div>
        
        <!-- Particules d'évolution -->
        <div v-if="badgeNeedsEvolution" class="evolution-particles">
          <div v-for="n in 8" :key="n" class="evolution-particle" :style="getEvolutionParticleStyle(n)"></div>
        </div>
        
        <img
          :src="getCurrentBadgeImage()"
          :alt="`Badge niveau ${calculateLevel()}`"
          class="current-badge-image"
          @click="handleBadgeClick"
          role="button"
          tabindex="0"
          @keydown.enter="handleBadgeClick"
        />
        
        <!-- Indicateur de progression des clics -->
        <div v-if="badgeNeedsEvolution" class="click-progress-ring">
          <svg width="100" height="100" class="progress-svg">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="rgba(255, 255, 255, 0.2)"
              stroke-width="3"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#FFD700"
              stroke-width="3"
              stroke-dasharray="283"
              :stroke-dashoffset="283 - (283 * badgeClickCount / 10)"
              class="progress-circle"
            />
          </svg>
        </div>
      </div>

      <div v-if="badgeNeedsEvolution" class="badge-evolution-text">
        <div class="evolution-instruction">
          <span class="click-icon">👆</span>
          Clique 10 fois sur la médaille pour l'évoluer
        </div>
        <div class="evolution-progress">
          <div class="progress-bar-evolution">
            <div class="progress-fill" :style="{width: (badgeClickCount / 10) * 100 + '%'}"></div>
          </div>
          <span class="progress-text">{{ badgeClickCount }}/10</span>
        </div>
      </div>

      <!-- Bouton Commencer à jouer -->
      <div class="play-button-container">
        <button
          class="play-button"
          @mouseenter="handlePlayButtonHover(true)"
          @mouseleave="handlePlayButtonHover(false)"
          @mousedown=";(playButtonPressed = true), $event.stopPropagation()"
          @mouseup=";(playButtonPressed = false), $event.stopPropagation()"
          @click="startPlaying()"
          :class="{ hovered: playButtonHovered, pressed: playButtonPressed, disabled: !areTutorialsCompleted }"
        >
          <div class="button-glow"></div>
          <span class="button-text">Commencer à jouer</span>
          <div class="button-border"></div>
        </button>
      </div>

      <!-- Animation de récompense -->
      <div class="achievement-popup" v-if="showAchievement">
        <div class="achievement-icon">🏆</div>
        <div class="achievement-text">
          <h3>Nouvelle réalisation!</h3>
          <p>{{ currentAchievement }}</p>
        </div>
      </div>

      <!-- Message d'avertissement pour les tutoriels -->
      <div class="tutorial-warning-popup" v-if="showTutorialWarning">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <h3>Tutoriel requis</h3>
          <p>Tu dois d'abord terminer le tutoriel avec Flamou avant de pouvoir commencer à jouer !</p>
        </div>
      </div>

      <rewards-component
        v-if="showRewardsModal"
        :current-theme="currentTheme"
        :animations-enabled="animationsEnabled"
        :progress="progress"
        :show-profile-guide="true"
        @close="closeRewardsModal"
        @generate-cv="handleGenerateCV"
        @view-profile="handleViewProfile"
      />

      <!-- Onglet de contrôle du thème -->
      <div class="theme-tab" @click="toggleThemeMenu" role="button" tabindex="0" @keydown.enter="toggleThemeMenu" aria-label="Changer le thème">
        <div class="theme-tab-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        </div>
      </div>

      <!-- Sélecteur de thème modifié avec transition -->
      <div
        class="theme-selector"
        :class="{ 'theme-selector-visible': themeMenuVisible }"
      >
        <div
          class="theme-option"
          v-for="theme in availableThemes"
          :key="theme.value"
          @click="changeTheme(theme.value)"
          :class="{ active: currentTheme === theme.value }"
          role="button"
          tabindex="0"
          @keydown.enter="changeTheme(theme.value)"
        >
          <div class="theme-icon" :class="theme.value"></div>
          <span>{{ theme.label }}</span>
        </div>

        <div
          class="theme-option animation-toggle"
          @click="toggleAnimations"
          :class="{ active: animationsEnabled }"
          role="button"
          tabindex="0"
          @keydown.enter="toggleAnimations"
        >
          <div class="theme-icon animation-icon"></div>
          <span>
            {{ animationsEnabled ? 'Animations ON' : 'Animations OFF' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpaceBackground from '@/components/SpaceBackground.vue'
import StaticBackgrounds from '@/components/StaticBackgrounds.vue'
import RewardsComponent from '@/components/RewardsComponent.vue'
import GuideAvatar from '@/components/GuideComponent.vue'
import AvatarDisplay from '@/components/AvatarDisplay.vue'
import UserJourneyService from '@/services/UserJourneyService.js'
import TutorialService from '@/services/TutorialService.js'
import BadgeService from '@/services/BadgeService.js'
import GameNavigationService from '@/services/GameNavigationService.js'
import { eventBus } from '@/utils/eventBus'

export default {
  name: 'UserDashboard',
  components: {
    SpaceBackground,
    StaticBackgrounds,
    RewardsComponent,
    GuideAvatar,
    AvatarDisplay,
  },
  data() {
    return {
      themeMenuVisible: false,
      currentTheme: 'cosmic',
      availableThemes: [
        { value: 'cosmic', label: 'Cosmic' },
        { value: 'ocean', label: 'Ocean' },
        { value: 'cyberpunk', label: 'Cyberpunk' },
        { value: 'forest', label: 'Forêt' },
        { value: 'snow', label: 'Neige' },
      ],
      progress: 0,
      gamesProgress: {},
      activeSection: null,
      avatarAnimating: false,
      showAvatarInteraction: false,
      activeModal: null,
      showAchievement: false,
      hasNewAchievement: false,
      showRewardsModal: false,
      showTutorialWarning: false,
      currentAchievement: '',
      animationsEnabled: true,
      playButtonHovered: false,
      playButtonPressed: false,
      achievements: [
        'Explorateur Curieux',
        'Premier Pas',
        'Maître du Temps',
        'Briseur de Barrières',
        'Esprit Créatif',
      ],
      guideContext: 'dashboard',
      guideMessage:
        "Salut ! Je suis Flamou. Voici ton tableau de bord.",
      guideOptions: [
        { text: 'Voir les fonctionnalités', action: 'explainDashboard' },
      ],
      guideForceShow: true,
      highlightAvatar: false,
      isFirstVisit: false,
      guidePosition: {
        position: 'fixed',
        top: '20px',
        left: '20px',
        zIndex: 2000,
      },
      guideTourStep: 0,
      highlightedElement: null,
      showGuideArrow: false,
      levelBadges: [
        {
          level: 1,
          name: 'Apprenti',
          description: 'Tu as fait tes premiers pas dans ton parcours !',
          image: () => require('@/assets/badges/badge1.png'),
        },
        {
          level: 2,
          name: 'Explorateur',
          description:
            'Tu commences à explorer les différentes activités disponibles.',
          image: () => require('@/assets/badges/badge2.png'),
        },
        {
          level: 3,
          name: 'Aventurier',
          description:
            "Tu progresses rapidement dans ton parcours d'apprentissage !",
          image: () => require('@/assets/badges/badge3.png'),
        },
        {
          level: 4,
          name: 'Champion',
          description:
            'Tu as atteint un niveau impressionnant, continue ainsi !',
          image: () => require('@/assets/badges/badge4.png'),
        },
        {
          level: 5,
          name: 'Maître',
          description: 'Tu es devenu un véritable maître dans ton parcours !',
          image: () => require('@/assets/badges/badge5.png'),
        },
        {
          level: 6,
          name: 'Légende',
          description: 'Tu es maintenant une légende !',
          image: () => require('@/assets/badges/badge6.png'),
        },
      ],
      activeBadgeTooltip: null,
      tooltipStyle: {
        top: '0px',
        left: '0px',
      },
      badgeClickCount: 0,
      badgeNeedsEvolution: false,
      lastEvolvedLevel: 0,
      showBadgeUnlockAnimation: false,
      newlyUnlockedBadge: null,
      userBadges: [],
      dashboardTutorialCompleted: false,
      profileTutorialCompleted: false,
    }
  },
  computed: {
    areTutorialsCompleted() {
      // Seul le tutoriel du Dashboard est requis pour activer le bouton
      return this.dashboardTutorialCompleted;
    }
  },
  created() {
    // Écouter les événements via eventBus
    eventBus.on('profile-tutorial-completed', () => {
      this.profileTutorialCompleted = true;
    });

    eventBus.on('dashboard-tutorial-completed', () => {
      this.dashboardTutorialCompleted = true;
    });
  },
  beforeUnmount() {
    eventBus.off('hide-dashboard-guide')
    eventBus.off('profile-tutorial-completed')
    eventBus.off('dashboard-tutorial-completed')
    eventBus.off('badge-unlocked', this.onBadgeUnlocked)
    window.removeEventListener('resize', this.updateGuidePosition)
    document.removeEventListener('visibilitychange', this.refreshProgressOnFocus)
    window.removeEventListener('focus', this.refreshProgressOnFocus)
    this.removeAllHighlights()
  },
  watch: {
    isFirstVisit(newVal) {
      if (newVal && !this.showRewardsModal) {
        this.guideForceShow = true
        this.$nextTick(() => {
          this.updateGuidePosition()
        })
      }
    },

    showRewardsModal(newVal) {
      if (!newVal && this.isFirstVisit) {
        this.guideForceShow = true
        this.$nextTick(() => {
          this.updateGuidePosition()
        })
      }
    },
  },
  methods: {
    /**
     * Initialise la progression des tutoriels depuis le backend
     */
    async initializeTutorialProgress() {
      try {
        const progress = await TutorialService.getProgress()

        // Charger les états du dashboard
        this.dashboardTutorialCompleted = progress.dashboardTutorialCompleted || false
        this.isFirstVisit = !progress.hasVisitedDashboard

        // Charger les états du guide si disponibles
        if (progress.guide) {
          this.guideTourStep = progress.guide.tourStep || 0
          this.guideForceShow = progress.guide.forceShow || false
          this.highlightAvatar = progress.guide.highlightAvatar || false
        }

        // Charger les états du profil
        if (progress.profileTour) {
          this.profileTutorialCompleted = progress.profileTourCompleted || false
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la progression:', error)
        // En cas d'erreur, utiliser les valeurs par défaut (localStorage en fallback)
        this.dashboardTutorialCompleted = localStorage.getItem('dashboard-tutorial-completed') === 'true'
        this.isFirstVisit = !localStorage.getItem('hasVisitedDashboard')
      }
    },

    /**
     * Sauvegarde la progression du dashboard dans le backend
     */
    async saveDashboardProgress() {
      try {
        await TutorialService.updateDashboardProgress({
          completed: this.dashboardTutorialCompleted,
          visited: true, // Le dashboard a été visité dès qu'on sauvegarde
          tourStep: this.guideTourStep,
          forceShow: this.guideForceShow,
          highlightAvatar: this.highlightAvatar,
        })
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la progression du dashboard:', error)
        // Fallback sur localStorage en cas d'erreur
        localStorage.setItem('dashboard-tutorial-completed', this.dashboardTutorialCompleted)
        localStorage.setItem('hasVisitedDashboard', 'true')
      }
    },

    /**
     * Gère le déblocage de badge depuis d'autres composants (synchronisation via eventBus)
     */
    onBadgeUnlocked({ badgeId, badge }) {

      // Mettre à jour localStorage (système legacy)
      try {
        const savedBadges = localStorage.getItem('userBadges')
        if (savedBadges) {
          const userBadges = JSON.parse(savedBadges)
          const badgeIndex = userBadges.findIndex(b => b.id === badgeId)

          if (badgeIndex !== -1) {
            userBadges[badgeIndex] = { ...userBadges[badgeIndex], ...badge, unlocked: true }
            localStorage.setItem('userBadges', JSON.stringify(userBadges))
            this.userBadges = userBadges
          }
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour locale du badge:', error)
      }

      // Rafraîchir la progression des jeux pour mettre à jour l'affichage
      this.calculateRealProgress()
    },

    /**
     * Vérifie si tous les jeux sont terminés pour débloquer le badge "Tous les jeux finis"
     */
    async checkAllGamesCompleted() {
      try {
        // Vérifier la progression des jeux depuis le backend
        const gameEndpoints = [
          '/games/scenario',
          '/games/shape-sequence',
          '/games/jobs',
          '/games/speed',
          '/games/skills',
          '/games/room-env',
        ];

        let allGamesCompleted = true;
        const completionStatus = {};

        // Vérifier chaque jeu
        const AuthService = (await import('@/services/AuthService.js')).default;

        for (const endpoint of gameEndpoints) {
          try {
            const response = await AuthService.fetchWithAuth({
              method: 'get',
              url: endpoint,
            });

            const completion = response.data.completion || 0;
            completionStatus[endpoint] = completion;

            if (completion < 1) {
              allGamesCompleted = false;
            }
          } catch (error) {
            // Jeu non commencé
            completionStatus[endpoint] = 0;
            allGamesCompleted = false;
          }
        }

        // Charger les badges depuis localStorage
        const savedBadges = localStorage.getItem('userBadges');
        if (!savedBadges) {
          return;
        }

        this.userBadges = JSON.parse(savedBadges);

        // Définition complète du badge "Tous les jeux finis"
        const badgeDefinition = {
          id: 9,
          title: 'Tous les jeux finis',
          description: 'Bravo ! Tu as terminé tous les jeux disponibles ! Tu es maintenant prêt à créer ton CV professionnel. 🌟',
          icon: '🏆',
          iconColor: '#4CAF50',
          unlocked: false,
        };

        // Trouver ou créer le badge dans userBadges
        let allGamesCompletedBadge = this.userBadges.find(badge => badge.id === 9);

        // Si le badge n'existe pas, le créer avec la définition complète
        if (!allGamesCompletedBadge) {
          allGamesCompletedBadge = { ...badgeDefinition };
          this.userBadges.push(allGamesCompletedBadge);
        } else {
          // S'assurer que le badge a toutes les propriétés nécessaires
          allGamesCompletedBadge = {
            ...badgeDefinition,
            ...allGamesCompletedBadge
          };
          // Mettre à jour dans le tableau
          const index = this.userBadges.findIndex(b => b.id === 9);
          this.userBadges[index] = allGamesCompletedBadge;
        }

        if (allGamesCompleted && allGamesCompletedBadge && !allGamesCompletedBadge.unlocked) {

          // Fermer toute animation en cours
          this.showBadgeUnlockAnimation = false;
          this.newlyUnlockedBadge = null;

          // Débloquer le badge
          allGamesCompletedBadge.unlocked = true;
          allGamesCompletedBadge.dateUnlocked = new Date().toISOString().split('T')[0];

          // Sauvegarder dans localStorage
          localStorage.setItem('userBadges', JSON.stringify(this.userBadges));

          // Déclencher l'animation finale après un délai plus long pour s'assurer que tout est chargé
          setTimeout(() => {
            this.newlyUnlockedBadge = allGamesCompletedBadge;
            this.showBadgeUnlockAnimation = true;
          }, 2000);

          // Mettre à jour la progression
          this.updateProgress();
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des badges:', error);
      }
    },

    /**
     * Met à jour la progression basée sur les badges débloqués
     */
    async updateProgress() {
      // Utiliser la nouvelle méthode de calcul de progression
      await this.calculateRealProgress();
    },

    /**
     * Ferme l'animation de badge
     */
    closeBadgeAnimation() {
      this.showBadgeUnlockAnimation = false;
      this.newlyUnlockedBadge = null;
    },

    /**
     * Génère un style aléatoire pour les particules de célébration
     */
    getParticleStyle() {
      const delay = Math.random() * 2;
      const duration = 3 + Math.random() * 2;
      const size = 4 + Math.random() * 4;
      const left = Math.random() * 100;
      
      return {
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        width: `${size}px`,
        height: `${size}px`,
        background: `radial-gradient(circle, ${this.getRandomColor()}, ${this.getRandomColor()})`
      };
    },

    /**
     * Génère une couleur aléatoire pour les particules
     */
    getRandomColor() {
      const colors = ['#FFD700', '#FF6347', '#FF4081', '#9C27B0', '#3F51B5', '#00BCD4'];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    /**
     * Génère un style pour les particules d'évolution
     */
    getEvolutionParticleStyle(index) {
      const angle = (index / 8) * 360;
      const delay = Math.random() * 2;
      const duration = 2 + Math.random();
      
      return {
        transform: `rotate(${angle}deg)`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      };
    },

    /**
     * Déclenche l'animation d'évolution spectaculaire
     */
    triggerEvolutionAnimation() {
      const badgeContainer = document.querySelector('.current-level-badge');
      const badgeElement = document.querySelector('.current-badge-image');
      
      if (badgeContainer && badgeElement) {
        // Animation de container
        badgeContainer.classList.add('evolution-explosion');
        
        // Animation de badge
        badgeElement.classList.add('badge-mega-evolve');
        
        // Créer des particules d'explosion
        this.createExplosionParticles();
        
        // Nettoyer après l'animation
        setTimeout(() => {
          badgeContainer.classList.remove('evolution-explosion');
          badgeElement.classList.remove('badge-mega-evolve');
        }, 2000);
      }
    },

    /**
     * Crée des particules d'explosion pour l'évolution
     */
    createExplosionParticles() {
      const container = document.querySelector('.dashboard-container');
      if (!container) return;
      
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div');
        particle.className = 'explosion-particle';
        
        const angle = (i / 30) * 360;
        const distance = 100 + Math.random() * 200;
        const size = 4 + Math.random() * 8;
        const color = this.getRandomColor();
        
        particle.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border-radius: 50%;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          animation: explosion-burst 2s ease-out forwards;
          --angle: ${angle}deg;
          --distance: ${distance}px;
          z-index: 1000;
        `;
        
        container.appendChild(particle);
        
        // Supprimer la particule après l'animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 2000);
      }
    },

    getCurrentBadgeImage() {
      // Obtenir le niveau actuel
      const currentLevel = this.calculateLevel()

      // Vérifier si le badge doit évoluer (seulement à partir du niveau 2)
      if (
        currentLevel > this.lastEvolvedLevel &&
        !this.badgeNeedsEvolution &&
        currentLevel > 1
      ) {
        this.badgeNeedsEvolution = true
        this.badgeClickCount = 0
      }

      // Trouver le badge le plus élevé débloqué
      const highestUnlockedBadge = this.levelBadges
        .filter(
          (badge) =>
            badge.level <=
            (this.badgeNeedsEvolution ? this.lastEvolvedLevel : currentLevel),
        )
        .sort((a, b) => b.level - a.level)[0]

      if (highestUnlockedBadge) {
        return highestUnlockedBadge.image(true)
      }

      // Badge par défaut si aucun badge n'est débloqué
      return this.levelBadges[0].image(false)
    },

    handleBadgeClick() {
      if (this.badgeNeedsEvolution && this.badgeClickCount < 10) {
        this.badgeClickCount++

        // Sauvegarder le compteur de clics
        localStorage.setItem('badgeClickCount', this.badgeClickCount.toString())

        // Animer le badge lors du clic
        const badgeElement = document.querySelector('.current-badge-image')
        if (badgeElement) {
          badgeElement.classList.add('badge-click-animation')
          setTimeout(() => {
            badgeElement.classList.remove('badge-click-animation')
          }, 300)
        }

        // Si 10 clics atteints, faire évoluer le badge
        if (this.badgeClickCount >= 10) {
          this.evolveBadge()
        }
      }
    },

    evolveBadge() {
      // Animation d'évolution spectaculaire
      this.triggerEvolutionAnimation()
      
      // Mettre à jour après l'animation
      setTimeout(() => {
        this.lastEvolvedLevel = this.calculateLevel()
        this.badgeNeedsEvolution = false
        this.badgeClickCount = 0

        // Sauvegarder dans localStorage
        localStorage.setItem('lastEvolvedLevel', this.lastEvolvedLevel.toString())
        
        // Nettoyer les données temporaires
        localStorage.removeItem('badgeNeedsEvolution')
        localStorage.removeItem('badgeClickCount')
      }, 2000)
    },

    // Méthode pour fermer le guide
    dismissGuide() {
      if (this.dashboardTutorialCompleted) {
        this.guideForceShow = false
        this.guideTourStep = 0
        this.guideMessage =
          'Clique sur moi pour revoir le tableau de bord.'
        this.guideOptions = [
          { text: 'Voir les fonctionnalités', action: 'explainDashboard' },
        ]
      } else {
        // If tutorial is not completed, keep Flamou visible and reset to initial tutorial prompt
        this.guideForceShow = true
        this.guideTourStep = 0
        this.guideMessage = "Salut ! Je suis Flamou. Voici ton tableau de bord."
        this.guideOptions = [{ text: 'Voir les fonctionnalités', action: 'explainDashboard' }]
      }

      this.highlightAvatar = false
      this.removeAllHighlights()
    },

    prepareGuideForRediscovery() {
      if (this.dashboardTutorialCompleted) {
        // Message adapté pour proposer de redécouvrir le dashboard
        this.guideMessage =
          'Revoir les fonctionnalités ?'
        this.guideOptions = [
          {
            text: 'Oui',
            action: 'restartDashboardTour',
            keepOpen: true,
          },
          { text: 'Non', action: 'dismissGuide' },
        ]
        this.guideForceShow = true
      }
    },

    resetGuideState() {
      this.guideTourStep = 0
      if (this.dashboardTutorialCompleted) {
        this.guideMessage =
          'Clique sur moi pour revoir le tableau de bord.'
        this.guideOptions = [
          { text: 'Voir les fonctionnalités', action: 'explainDashboard' },
        ]
        this.guideForceShow = false
      } else {
        // If tutorial is not completed, keep Flamou visible and reset to initial tutorial prompt
        this.guideMessage = "Salut ! Je suis Flamou. Voici ton tableau de bord."
        this.guideOptions = [
          { text: 'Voir les fonctionnalités', action: 'explainDashboard' },
        ]
        this.guideForceShow = true
      }
      this.removeAllHighlights()
    },

    restartDashboardTour() {
      // Réinitialiser l'étape du tutoriel
      this.guideTourStep = 0

      // Réactiver le guide et commencer le tutoriel
      this.guideForceShow = true
      this.explainDashboard()

      // Mettre à jour la position du guide si nécessaire
      this.$nextTick(() => {
        this.updateGuidePosition()
      })
    },

    async advanceTutorial() {
      // Incrémenter l'étape du tutoriel
      this.guideTourStep++

      // Sauvegarder la progression dans le backend (non-bloquant pour réactivité)
      this.saveDashboardProgress()

      // Retirer les surlignages précédents
      this.removeAllHighlights()

      // Définir le contenu en fonction de l'étape actuelle
      switch (this.guideTourStep) {
        case 1: // Explications sur l'anneau de progression autour de l'avatar
          this.guideMessage =
            "Ton avatar. L'anneau montre ta progression."
          this.guideOptions = [
            { text: 'Suivant', action: 'advanceTutorial', keepOpen: true },
          ]
          this.highlightElement('.progress-ring-container', true)
          break

        case 2: // Explications sur le bouton des thèmes
          this.guideMessage =
            'Change le thème et les animations ici.'
          this.guideOptions = [
            { text: 'Suivant', action: 'advanceTutorial', keepOpen: true },
          ]
          this.highlightElement('.theme-tab')
          break

        case 3: // Explications sur le bouton d'accessibilité
          this.guideMessage =
            "Adapte la taille du texte et les couleurs."
          this.guideOptions = [
            { text: 'Suivant', action: 'advanceTutorial', keepOpen: true },
          ]
          this.highlightElement('.accessibility-widget')
          break

        case 4: // Explications sur les boutons de navigation
          this.guideMessage =
            'Utilise ces flèches pour naviguer dans la page progressivement.'
          this.guideOptions = [
            { text: 'Suivant', action: 'advanceTutorial', keepOpen: true },
          ]
          this.highlightElement('.scroll-navigation-buttons')
          break

        case 5: // Explications sur le bouton plein écran
          this.guideMessage =
            'Mode plein écran.'
          this.guideOptions = [
            { text: 'Suivant', action: 'advanceTutorial', keepOpen: true },
          ]
          this.highlightElement('.fullscreen-button')
          break

        case 6: // Explications sur le bouton "Commencer à jouer" (avant-dernière étape)
          this.guideMessage =
            'Ce bouton permet de commencer à jouer aux jeux.'
          this.guideOptions = [
            { text: 'Suivant', action: 'advanceTutorial', keepOpen: true },
          ]
          this.highlightElement('.play-button')
          break

        case 7: // Invitation à débloquer le premier badge (dernière étape)
          this.guideMessage =
            'Découvre ton profil pour débloquer ton premier badge.'
          this.guideOptions = [
            {
              text: 'Comment accéder ?',
              action: 'showProfileHelp',
              keepOpen: true,
            },
          ]
          break

        default:
          this.dismissGuide()
          break
      }
    },

    highlightElement(selector, isSpecial = false) {
      const element = document.querySelector(selector)
      if (element) {
        // Retirer les surlignages précédents
        this.removeAllHighlights()

        // Obtenir les coordonnées de l'élément
        const rect = element.getBoundingClientRect()

        // Créer un élément de surbrillance
        const highlight = document.createElement('div')
        highlight.className = 'guide-highlight'

        // Détecter si l'élément est rond (avatar, boutons ronds)
        const isRound =
          selector.includes('avatar-container') ||
          selector.includes('progress-ring-container') ||
          selector.includes('theme-tab') ||
          selector.includes('theme-icon') ||
          selector.includes('fullscreen-button') ||
          selector.includes('accessibility-widget') ||
          selector.includes('scroll-navigation-buttons')

        // Positionner la surbrillance
        highlight.style.position = 'fixed'
        highlight.style.top = `${rect.top - 5}px`
        highlight.style.left = `${rect.left - 5}px`
        highlight.style.width = `${rect.width + 10}px`
        highlight.style.height = `${rect.height + 10}px`
        highlight.style.border = '3px solid #76ff03'

        // Appliquer un rayon de bordure approprié selon la forme de l'élément
        if (isRound) {
          // Pour les éléments ronds comme l'avatar, utiliser 50%
          highlight.style.borderRadius = '50%'
        } else if (selector.includes('play-button')) {
          // Pour le bouton jouer qui a un rayon précis
          highlight.style.borderRadius = '30px'
        } else {
          // Pour les autres éléments, conserver un rayon par défaut
          highlight.style.borderRadius = '12px'
        }

        highlight.style.boxShadow = '0 0 15px rgba(118, 255, 3, 0.7)'
        highlight.style.pointerEvents = 'none'
        highlight.style.zIndex = '1050'

        // Ajouter une animation si c'est l'élément spécial (avatar)
        if (isSpecial) {
          highlight.style.animation = 'highlight-pulse 2s ease-out infinite'

          // Ajouter une flèche si nécessaire
          if (this.showGuideArrow) {
            const arrow = document.createElement('div')
            arrow.className = 'guide-arrow'
            arrow.textContent = '↓'
            arrow.style.position = 'fixed'
            arrow.style.top = `${rect.top - 40}px`
            arrow.style.left = `${rect.left + rect.width / 2}px`
            arrow.style.transform = 'translateX(-50%)'
            arrow.style.color = '#76ff03'
            arrow.style.fontSize = '36px'
            arrow.style.fontWeight = 'bold'
            arrow.style.textShadow = '0 0 10px rgba(118, 255, 3, 0.7)'
            arrow.style.animation = 'bounce 2s ease infinite'
            arrow.style.zIndex = '1051'

            document.body.appendChild(arrow)
          }
        }

        // Ajouter au DOM
        document.body.appendChild(highlight)

        // Stocker l'élément surligné
        this.highlightedElement = selector

        // Ne pas utiliser scrollIntoView pour les éléments déjà visibles
        // ou pour les éléments en bas de l'écran (thème, bouton accessibilité, etc.)
        const isBottomElement =
          selector.includes('theme') ||
          selector.includes('fullscreen') ||
          selector.includes('accessibility') ||
          selector.includes('play-button')

        // Vérifier si l'élément est déjà visible dans la fenêtre
        const isVisible =
          rect.top >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.left >= 0 &&
          rect.right <= window.innerWidth

        // Faire défiler uniquement si nécessaire et pas pour les éléments du bas
        if (!isBottomElement && !isVisible) {
          // Défilement plus doux avec une marge
          window.scrollTo({
            top: window.scrollY + rect.top - 200, // 200px de marge en haut
            behavior: 'smooth',
          })
        }
      }
    },

    // Méthode pour retirer tous les surlignages
    removeAllHighlights() {
      // Retirer les surlignages
      const highlights = document.querySelectorAll('.guide-highlight')
      highlights.forEach((el) => el.remove())

      // Retirer les flèches
      const arrows = document.querySelectorAll('.guide-arrow')
      arrows.forEach((el) => el.remove())

      // Réinitialiser l'état
      this.highlightedElement = null
      this.showGuideArrow = false
    },

    // Méthode pour expliquer le bouton de jeu
    explainPlayButton() {
      this.guideMessage =
        "Clique sur 'Commencer à jouer' en bas."
      this.guideOptions = [
        { text: 'Compris', action: 'dismissGuide', keepOpen: true },
      ]
      // Désactiver la mise en évidence de l'avatar
      this.highlightAvatar = false

      // Scroll automatique vers le bouton de jeu si nécessaire
      this.$nextTick(() => {
        const playButton = document.querySelector('.play-button')
        if (playButton) {
          playButton.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },

    showProfileHelp() {
      this.guideMessage =
        "Clique sur ton avatar pour voir ton profil."
      this.guideOptions = [
        {
          text: 'Compris',
          action: 'finalizeHelp',
          keepOpen: false,
        },
      ]
      // Conserver la mise en évidence de l'avatar
      this.highlightAvatar = true
      this.showGuideArrow = true
      this.highlightElement('.avatar-container', true)

      // Scroll automatique vers l'avatar si nécessaire
      this.$nextTick(() => {
        const avatarElement = document.querySelector('.avatar-container')
        if (avatarElement) {
          avatarElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },

    async finalizeHelp() {
      // Fermer la bulle du guide
      this.guideForceShow = false

      // Marquer le tutoriel du dashboard comme terminé
      this.dashboardTutorialCompleted = true

      // Sauvegarder dans le backend
      await this.saveDashboardProgress()

      // Émettre l'événement via eventBus
      eventBus.emit('dashboard-tutorial-completed')

      // Conserver la mise en évidence et la flèche pour guider l'utilisateur
      // vers l'avatar (ne pas les supprimer)
      this.highlightAvatar = true
      this.showGuideArrow = true

      // Réappliquer la mise en évidence pour s'assurer qu'elle est visible
      this.$nextTick(() => {
        this.highlightElement('.avatar-container', true)
      })
    },

    forceGuideDisplay() {
      if (this.isFirstVisit && !this.showRewardsModal) {
        this.guideForceShow = true
        this.updateGuidePosition()

        // Émettre un événement via eventBus pour informer le guide qu'il doit s'afficher
        eventBus.emit('force-guide-display')
      }
    },
    updateGuidePosition() {
      // Si le guide est en mode conversation (bulle visible)
      if (this.guideForceShow && this.isFirstVisit) {
        // Attendre que le DOM soit rendu
        this.$nextTick(() => {
          const avatarElement = document.querySelector('.avatar-container')
          if (avatarElement) {
            const rect = avatarElement.getBoundingClientRect()

            // Positionner le guide à droite de l'avatar et centré verticalement
            this.guidePosition = {
              position: 'fixed',
              top: `${rect.top + rect.height / 2 - 30}px`,
              left: `${rect.right + 20}px`,
              zIndex: 2000,
            }
          }
        })
      } else {
        // Position par défaut quand la bulle n'est pas affichée
        this.guidePosition = {
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 2000,
        }
      }
    },
    // Nouvelle méthode pour expliquer le dashboard
    explainDashboard() {
      // Réinitialiser le compteur d'étapes si l'utilisateur a terminé le tour
      if (this.profileTutorialCompleted) {
        // Réinitialiser les options pour qu'elles soient disponibles après
        this.guideTourStep = 0

        // Définir un message d'introduction plus court pour les utilisateurs expérimentés
        this.guideMessage =
          'Ton espace : avatar, progression, thèmes et jeux.'
        this.guideOptions = [
          {
            text: 'Voir les fonctionnalités',
            action: 'advanceTutorial',
            keepOpen: true,
          },
          { text: 'Jouer', action: 'startPlaying', keepOpen: true },
          {
            text: 'Compris',
            action: 'dismissGuide',
            keepOpen: true,
          },
        ]
      } else {
        // Comportement standard pour les nouveaux utilisateurs
        this.guideTourStep = 0
        this.advanceTutorial()
      }
    },

    // Méthode pour gérer les options sélectionnées dans le guide
    handleGuideOptionSelected(option) {
      if (option.action === 'finalizeHelp') {
        // Action spéciale pour finaliser l'aide et garder les effets visuels
        this.finalizeHelp()
        return
      }

      // Pour toutes les autres actions
      if (option.action && typeof this[option.action] === 'function') {
        this[option.action]()
      } else {
        console.warn(`L'action "${option.action}" n'est pas définie.`)
      }
    },

    toggleThemeMenu() {
      this.themeMenuVisible = !this.themeMenuVisible

      // Ajout d'une sensation tactile
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50)
      }

      // Stocker la préférence dans localStorage
      localStorage.setItem(
        'theme-menu-visible',
        this.themeMenuVisible.toString(),
      )
    },

    changeTheme(theme) {
      this.currentTheme = theme

      // Optional: store the selected theme in localStorage to persist across sessions
      localStorage.setItem('dashboard-theme', theme)

      // Optional: trigger achievement for first theme change
      if (!this.themeChangeAchieved && theme !== 'cosmic') {
        this.themeChangeAchieved = true
      }
    },

    // Keep all existing methods
    calculateProgressOffset() {
      const circumference = 2 * Math.PI * 120
      return circumference - (circumference * this.progress) / 100
    },

    calculateLevel() {
      // Nouveau calcul : 1 niveau tous les 16.67% de progression (6 niveaux max pour 100%)
      // Niveau 1: 0-16%
      // Niveau 2: 17-33%
      // Niveau 3: 34-50%
      // Niveau 4: 51-66%
      // Niveau 5: 67-83%
      // Niveau 6: 84-100%
      return Math.min(Math.floor(this.progress / 16.67) + 1, 6)
    },

    /**
     * Calcule la progression totale basée sur les badges débloqués
     * Utilise la même logique que RewardsComponent : (badges débloqués / 10) * 100
     */
    async calculateRealProgress() {
      try {
        // Charger les badges depuis le backend (même logique que RewardsComponent)
        const { badges } = await BadgeService.getUserBadges();

        // Compter les badges débloqués
        const unlockedBadgesCount = badges.filter(badge => badge.unlocked).length;
        const totalBadgesCount = badges.length; // 10 badges au total

        // Calculer la progression : (badges débloqués / total badges) * 100
        const finalProgress = (unlockedBadgesCount / totalBadgesCount) * 100;
        this.progress = Math.round(finalProgress);

        // Charger aussi la progression des jeux pour la méthode startPlaying()
        const gameEndpoints = [
          { endpoint: '/games/scenario', weight: 1 },
          { endpoint: '/games/shape-sequence', weight: 1 },
          { endpoint: '/games/jobs', weight: 1 },
          { endpoint: '/games/speed', weight: 1 },
          { endpoint: '/games/skills', weight: 1 },
          { endpoint: '/games/room-env', weight: 1 },
        ];

        const progressData = {};
        const AuthService = (await import('@/services/AuthService.js')).default;

        for (const { endpoint } of gameEndpoints) {
          try {
            const response = await AuthService.fetchWithAuth({
              method: 'get',
              url: endpoint,
            });
            progressData[endpoint] = response.data.completion || 0;
          } catch (error) {
            // Jeu non commencé
            progressData[endpoint] = 0;
          }
        }

        this.gamesProgress = progressData;

        // Vérifier si le badge a besoin d'évoluer
        this.checkBadgeEvolution();

        // Vérifier si tous les jeux sont terminés pour débloquer le badge final
        setTimeout(() => {
          this.checkAllGamesCompleted();
        }, 500);

        // Synchroniser avec le RewardsComponent
        this.syncWithRewardsComponent();

        return this.progress;
      } catch (error) {
        console.error('Erreur lors du calcul de la progression:', error);
        // Fallback sur localStorage en cas d'erreur
        try {
          const savedBadges = localStorage.getItem('userBadges');
          if (savedBadges) {
            const badges = JSON.parse(savedBadges);
            const unlockedCount = badges.filter(b => b.unlocked).length;
            this.progress = Math.round((unlockedCount / 10) * 100);
          }
        } catch (fallbackError) {
          console.error('Erreur lors du fallback localStorage:', fallbackError);
        }
        return this.progress;
      }
    },

    /**
     * Vérifie si le badge doit évoluer
     */
    checkBadgeEvolution() {
      const currentLevel = this.calculateLevel();
      
      // S'assurer que lastEvolvedLevel est bien défini
      if (this.lastEvolvedLevel === undefined || this.lastEvolvedLevel === null) {
        this.lastEvolvedLevel = 0;
      }
      
      // Vérifier si le badge doit évoluer (seulement à partir du niveau 2)
      if (currentLevel > this.lastEvolvedLevel && !this.badgeNeedsEvolution && currentLevel > 1) {
        this.badgeNeedsEvolution = true;
        this.badgeClickCount = 0;
        
        // Sauvegarder immédiatement pour éviter les doublons
        localStorage.setItem('badgeNeedsEvolution', 'true');
        localStorage.setItem('badgeClickCount', '0');
      }
    },

    generateParticleStyle() {
      const duration = 1 + Math.random() * 1.5
      const delay = Math.random() * 0.5
      const size = 3 + Math.random() * 4
      const halfSize = size / 2

      return {
        left: `calc(50% - ${halfSize}px)`,
        top: `calc(50% - ${halfSize}px)`,
        width: `${size}px`,
        height: `${size}px`,
        transform: 'scale(0)',
        opacity: '0',
        animation: `particleExpand ${duration}s ease ${delay}s infinite`,
      }
    },

    // Génère un style aléatoire pour les particules d'arrière-plan
    randomParticleStyle() {
      return {
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${5 + Math.random() * 10}s`,
        opacity: Math.random() * 0.5,
        transform: `scale(${0.5 + Math.random() * 1.5})`,
      }
    },

    async interactWithAvatar() {
      // Si l'utilisateur clique sur l'avatar pendant le tutoriel (highlightAvatar est activé)
      // alors marquer le tutoriel comme terminé
      if (this.highlightAvatar && !this.dashboardTutorialCompleted) {
        this.dashboardTutorialCompleted = true
        await this.saveDashboardProgress()
        eventBus.emit('dashboard-tutorial-completed')
      }

      this.avatarAnimating = true
      this.showRewardsModal = true

      // Supprimer le highlight de l'avatar
      this.highlightAvatar = false
      this.showGuideArrow = false

      // Supprimer tous les highlights visuels
      this.removeAllHighlights()

      // Animation plus longue pour un meilleur effet
      setTimeout(() => {
        this.avatarAnimating = false
      }, 1000)

      // Mettre à jour l'étape dans le service de parcours utilisateur
      try {
        if (typeof UserJourneyService !== 'undefined') {
          UserJourneyService.updateStep(UserJourneyService.STEPS.PROFILE_INTRO)
        }
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour de l'étape du parcours:",
          error,
        )
      }

      // Mettre à jour la position du guide si nécessaire
      if (this.isFirstVisit && !this.showRewardsModal) {
        this.updateGuidePosition()
      }

      // Envoyer un événement via eventBus
      eventBus.emit('profile-opened')

      // Masquer le guide du dashboard quand on ouvre la modal du profil
      this.guideForceShow = false
      eventBus.emit('hide-dashboard-guide')
    },

    // Nouvelle méthode pour fermer le modal de récompenses
    closeRewardsModal() {
      this.showRewardsModal = false
      if (this.isFirstVisit) {
        this.guideForceShow = true
      }
    },

    handleGenerateCV() {
      // Déclenche un accomplissement et ferme le modal
      this.closeRewardsModal()
    },

    handleViewProfile() {
      this.closeRewardsModal()
    },

    // Voir les réalisations
    viewAchievements() {
      this.showAvatarInteraction = false
      this.activeModal = 'achievements'
    },

    // Déclenche une animation de réalisation
    triggerAchievement(achievement) {
      this.currentAchievement = achievement
      this.showAchievement = true
      this.hasNewAchievement = true

      // Animation de progression
      const oldProgress = this.progress
      const newProgress = Math.min(100, oldProgress + 5)

      // Animation progressive du changement
      const step = 0.5
      const duration = 2000 // 2 secondes
      const steps = (newProgress - oldProgress) / step
      const interval = duration / steps

      const progressAnimation = setInterval(() => {
        if (this.progress < newProgress) {
          this.progress += step
        } else {
          clearInterval(progressAnimation)
        }
      }, interval)

      // Faire disparaître l'animation après 3 secondes
      setTimeout(() => {
        this.showAchievement = false
      }, 3000)

      // Réinitialiser l'effet de fond
      setTimeout(() => {
        this.hasNewAchievement = false
      }, 4000)
    },

    toggleAnimations() {
      this.animationsEnabled = !this.animationsEnabled

      localStorage.setItem(
        'dashboard-animations',
        this.animationsEnabled.toString(),
      )

      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50)
      }

      this.$emit('toggle-animations', !this.animationsEnabled)
    },

    handlePlayButtonHover(isHovered) {
      this.playButtonHovered = isHovered
      if (!isHovered) {
        this.playButtonPressed = false
      }
    },

    async startPlaying() {
      // Vérifier si les tutoriels sont terminés
      if (!this.areTutorialsCompleted) {
        // Afficher le message d'avertissement
        this.showTutorialWarning = true

        // Faire disparaître le message après 4 secondes
        setTimeout(() => {
          this.showTutorialWarning = false
        }, 4000)

        return; // Ne rien faire si les tutoriels ne sont pas terminés
      }

      // Trigger achievement
      this.triggerAchievement('Premier Pas')

      // Utiliser le service de navigation pour sélectionner et naviguer vers le prochain jeu
      await GameNavigationService.navigateToNextGame(this.$router)
    },

    /**
     * Actualise la progression quand l'utilisateur revient sur le dashboard
     */
    async refreshProgressOnFocus() {
      if (document.visibilityState === 'visible') {
        await this.calculateRealProgress();
      }
    },

    /**
     * Synchronise la progression avec le RewardsComponent
     */
    syncWithRewardsComponent() {
      // Émettre un événement pour notifier le RewardsComponent
      eventBus.emit('dashboard-progress-updated', {
        progress: this.progress,
        level: this.calculateLevel(),
        gamesProgress: this.gamesProgress
      });
    },

    /**
     * Charge le niveau d'évolution depuis localStorage
     */
    loadBadgeEvolutionState() {
      // Charger le dernier niveau évolué
      const savedLevel = localStorage.getItem('lastEvolvedLevel');
      if (savedLevel) {
        this.lastEvolvedLevel = parseInt(savedLevel, 10);
      } else {
        this.lastEvolvedLevel = 0;
      }

      // Charger l'état d'évolution en cours
      const needsEvolution = localStorage.getItem('badgeNeedsEvolution');
      if (needsEvolution === 'true') {
        this.badgeNeedsEvolution = true;
        const savedClickCount = localStorage.getItem('badgeClickCount');
        this.badgeClickCount = savedClickCount ? parseInt(savedClickCount, 10) : 0;
      }
    },
  },
  async mounted() {
    // Initialiser la progression des tutoriels depuis le backend
    await this.initializeTutorialProgress()

    // Charger l'état d'évolution du badge
    this.loadBadgeEvolutionState();

    // Check if the showBadges query parameter exists
    if (
      this.$route.query.showBadges === 'true' ||
      this.$route.query.showBadges === true
    ) {
      this.showRewardsModal = true
      const newQuery = { ...this.$route.query }
      delete newQuery.showBadges
      this.$router.replace({ query: newQuery })
    }

    // Calculer la progression réelle des jeux
    await this.calculateRealProgress();

    // Vérifier les badges après le chargement de la progression (avec un délai pour s'assurer que les données sont à jour)
    setTimeout(async () => {
      await this.checkAllGamesCompleted();
    }, 1000);

    let savedTheme = localStorage.getItem('dashboard-theme')
    
    // Si pas de thème sauvegardé, vérifier dans user_profile (pour les utilisateurs de l'onboarding)
    if (!savedTheme) {
      try {
        const userProfile = localStorage.getItem('user_profile')
        if (userProfile) {
          const profile = JSON.parse(userProfile)
          if (profile.theme) {
            savedTheme = profile.theme
            // Sauvegarder le thème dans le bon endroit pour la prochaine fois
            localStorage.setItem('dashboard-theme', savedTheme)
          }
        }
      } catch (error) {
        console.warn('Error parsing user profile for theme:', error)
      }
    }
    
    if (
      savedTheme &&
      this.availableThemes.some((theme) => theme.value === savedTheme)
    ) {
      this.currentTheme = savedTheme
    }

    const savedAnimationPref = localStorage.getItem('dashboard-animations')
    if (savedAnimationPref !== null) {
      this.animationsEnabled = savedAnimationPref === 'true'
    }

    this.themeChangeAchieved = false

    // Mettre à jour la position du guide
    this.updateGuidePosition()

    // Recalculer la position lors du redimensionnement de la fenêtre
    window.addEventListener('resize', this.updateGuidePosition)

    // Écouter la visibilité de la page pour actualiser la progression
    document.addEventListener('visibilitychange', this.refreshProgressOnFocus)

    // Écouter les événements de focus sur la fenêtre
    window.addEventListener('focus', this.refreshProgressOnFocus)

    // Écouter les événements de déblocage de badges pour synchronisation
    eventBus.on('badge-unlocked', this.onBadgeUnlocked)

    if (this.isFirstVisit && !this.showRewardsModal) {
      // S'assurer que guideForceShow est bien à true
      this.guideForceShow = true

      // Attendre un peu pour s'assurer que le composant est bien monté
      setTimeout(() => {
        // Déclencher à nouveau la mise à jour du message pour forcer l'affichage
        this.guideMessage = this.guideMessage + ' ' // Ajouter un espace pour forcer le changement
        this.guideForceShow = true
      }, 300)
    }

    // Ajouter ces styles pour les animations du guide
    const style = document.createElement('style')
    style.textContent = `
      @keyframes highlight-pulse {
        0% {
          opacity: 0.7;
          box-shadow: 0 0 15px rgba(118, 255, 3, 0.5);
          transform: scale(0.99);
        }
        50% {
          opacity: 0.9;
          box-shadow: 0 0 20px rgba(118, 255, 3, 0.8);
          transform: scale(1);
        }
        100% {
          opacity: 0.7;
          box-shadow: 0 0 15px rgba(118, 255, 3, 0.5);
          transform: scale(0.99);
        }
      }

      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateX(-50%) translateY(0);
        }
        40% {
          transform: translateX(-50%) translateY(-15px);
        }
        60% {
          transform: translateX(-50%) translateY(-7px);
        }
      }
    `
    document.head.appendChild(style)
  },
}
</script>

<style scoped>
.dashboard {
  position: relative;
  width: 100%;
  height: 100vh;
  color: white;
  overflow: hidden;
  font-family: 'Nunito', sans-serif;
  transition: all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  background: transparent;
}

.dashboard-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(
      circle at 30% 40%,
      rgba(76, 0, 153, 0.2) 0%,
      rgba(76, 0, 153, 0) 50%
    ),
    radial-gradient(
      circle at 70% 60%,
      rgba(63, 0, 113, 0.2) 0%,
      rgba(63, 0, 113, 0) 60%
    ),
    radial-gradient(
      circle at 50% 50%,
      rgba(0, 51, 102, 0.2) 0%,
      rgba(0, 51, 102, 0) 70%
    );
  filter: blur(30px);
  opacity: 0.8;
  z-index: 0;
  animation: nebulaShift 60s ease-in-out infinite alternate;
}

.dashboard-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  z-index: 10;
  padding-top: 15vh;
}

.theme-selector {
  position: absolute;
  bottom: 20px;
  left: 80px;
  display: flex;
  gap: 15px;
  background: rgba(30, 30, 45, 0.7);
  border-radius: 12px;
  padding: 10px 15px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  z-index: 20;
  transform: translateY(20px) translateX(25px);
  opacity: 0;
  visibility: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform-origin: bottom left;
  pointer-events: none;
}

.theme-selector-visible {
  transform: translateY(0) translateX(0);
  opacity: 1;
  visibility: visible;
  pointer-events: all;
}

.theme-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px;
  border-radius: 8px;
}

.theme-option:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
}

.theme-option.active {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 15px rgba(124, 77, 255, 0.5);
}

.theme-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-bottom: 5px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.theme-option:hover .theme-icon {
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.theme-option.active .theme-icon {
  border-color: white;
  transform: scale(1.1);
}

.theme-icon.cosmic {
  background: linear-gradient(135deg, #7c4dff 0%, #0d47a1 100%);
  box-shadow: 0 0 10px rgba(124, 77, 255, 0.5);
}

.theme-icon.ocean {
  background: linear-gradient(135deg, #4fc3f7 0%, #0d47a1 100%);
  box-shadow: 0 0 10px rgba(79, 195, 247, 0.5);
}

.theme-icon.cyberpunk {
  background: linear-gradient(135deg, #ff4081 0%, #ab47bc 100%);
  box-shadow: 0 0 10px rgba(255, 64, 129, 0.5);
}

.theme-icon.forest {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  box-shadow: 0 0 10px rgba(46, 125, 50, 0.5);
}

.theme-icon.snow {
  background: linear-gradient(135deg, #6ebeff 0%, #eff5ff 100%);
  box-shadow: 0 0 10px rgba(144, 202, 249, 0.5);
}

.theme-option span {
  font-size: 12px;
  opacity: 0.8;
  transition: all 0.3s ease;
}

.theme-option:hover span,
.theme-option.active span {
  opacity: 1;
}

.theme-tab {
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: 52px;
  height: 52px;
  background: rgba(30, 30, 45, 0.7);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 30;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.theme-tab:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.4);
  background: rgba(40, 40, 60, 0.8);
}

.theme-tab:active {
  transform: scale(0.95);
}

.theme-tab-icon {
  color: white;
  width: 24px;
  height: 24px;
  animation: rotateIcon 10s linear infinite;
}

@keyframes rotateIcon {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animation-toggle {
  margin-left: 10px;
  background-color: rgba(30, 30, 45, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.animation-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
}

.animation-toggle.active {
  border-color: #4fc3f7;
  box-shadow: 0 0 15px rgba(79, 195, 247, 0.5);
}

.animation-toggle:not(.active) {
  opacity: 0.7;
}

.animation-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  color: #fff;
}

.animation-icon i {
  transition: all 0.3s ease;
}

.animation-toggle.active .animation-icon i {
  color: #4fc3f7;
}

.animation-toggle:not(.active) .animation-icon i {
  color: #aaa;
}

/* Add animation for changing themes */
@keyframes nebulaShift {
  0% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.2) rotate(5deg);
  }
  100% {
    transform: scale(1) rotate(10deg);
  }
}

/* Container principal pour tous les éléments spatiaux */
.space-elements {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

/* Animation badge débloqué */
.badge-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  animation: fadeIn 0.5s ease-out;
}

.badge-unlock-animation {
  background-color: #fff;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  max-width: 450px;
  box-shadow: 0 0 30px rgba(249, 71, 136, 0.6);
  animation: scaleIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.badge-unlock-animation.special-celebration {
  background: linear-gradient(135deg, #FFD700, #FFA500, #FF6347);
  box-shadow: 0 0 50px rgba(255, 215, 0, 0.8);
  animation: scaleIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), celebration-glow 2s ease-in-out infinite alternate;
}

.badge-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
}

.badge-icon {
  font-size: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 2px 10px rgba(255, 255, 255, 0.8);
  animation: 
    badge-entrance 1s cubic-bezier(0.34, 1.56, 0.64, 1),
    badge-float 3s ease-in-out infinite,
    badge-sparkle 2s ease-in-out infinite;
  position: relative;
  z-index: 2;
}

.badge-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.8) 50%,
    transparent 70%
  );
  animation: shine-sweep 2s ease-in-out;
  z-index: 3;
  pointer-events: none;
}

.badge-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 180px;
  height: 180px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    rgba(249, 71, 136, 0.3) 0%,
    rgba(249, 71, 136, 0.1) 50%,
    transparent 70%
  );
  border-radius: 50%;
  animation: glow-pulse 2s ease-in-out infinite;
  z-index: 1;
}

.special-celebration .badge-icon {
  background: radial-gradient(circle, #FFD700, #FFA500);
  animation: 
    badge-entrance 1s cubic-bezier(0.34, 1.56, 0.64, 1),
    trophy-dance 2s ease-in-out infinite,
    special-sparkle 1.5s ease-in-out infinite;
  box-shadow: 
    0 0 30px rgba(255, 215, 0, 0.8),
    0 0 60px rgba(255, 165, 0, 0.4);
}

.special-celebration .badge-glow {
  background: radial-gradient(
    circle,
    rgba(255, 215, 0, 0.4) 0%,
    rgba(255, 165, 0, 0.2) 50%,
    transparent 70%
  );
  animation: special-glow-pulse 1.5s ease-in-out infinite;
}

.text-container {
  margin-bottom: 30px;
}

.animated-text {
  opacity: 0;
  transform: translateY(20px);
  animation: text-slide-up 0.6s ease-out forwards;
}

.badge-unlock-animation h2 {
  color: #FF4081;
  font-size: 2.2rem;
  margin-bottom: 15px;
  font-weight: 700;
}

.special-celebration h2 {
  color: #8B4513;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  animation: text-slide-up 0.6s ease-out forwards, text-celebration 2s ease-in-out infinite alternate;
}

.badge-unlock-animation h3 {
  color: #333;
  font-size: 1.6rem;
  margin-bottom: 20px;
  font-weight: 600;
}

.special-celebration h3 {
  color: #654321;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}

.badge-unlock-animation p {
  color: #666;
  margin-bottom: 25px;
  font-size: 1.1rem;
  line-height: 1.5;
}

.special-celebration p {
  color: #4B3621;
  font-weight: 500;
  font-size: 1.2rem;
}

.close-animation-btn {
  background-color: #FF4081;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(255, 64, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.animated-btn {
  opacity: 0;
  transform: translateY(20px) scale(0.9);
  animation: button-entrance 0.6s ease-out forwards;
}

.close-animation-btn.celebration-btn {
  background: linear-gradient(135deg, #FF6347, #FFD700);
  color: #8B4513;
  font-weight: bold;
  animation: button-entrance 0.6s ease-out forwards, button-celebration 2s ease-in-out infinite alternate;
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.5);
}

.close-animation-btn:hover {
  background-color: #D81B60;
  transform: scale(1.08) translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 64, 129, 0.4);
}

.close-animation-btn.celebration-btn:hover {
  background: linear-gradient(135deg, #FFD700, #FF6347);
  transform: scale(1.12) translateY(-3px);
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.7);
}

.celebration-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.celebration-particles .particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: radial-gradient(circle, #FFD700, #FF6347);
  border-radius: 50%;
  animation: particle-float 3s ease-out infinite;
}

/* Animations spéciales */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  0% { 
    transform: scale(0.3) rotate(-10deg); 
    opacity: 0; 
  }
  50% { 
    transform: scale(1.05) rotate(2deg); 
    opacity: 0.8; 
  }
  100% { 
    transform: scale(1) rotate(0deg); 
    opacity: 1; 
  }
}

@keyframes celebration-glow {
  0% { box-shadow: 0 0 50px rgba(255, 215, 0, 0.8); }
  100% { box-shadow: 0 0 70px rgba(255, 165, 0, 1); }
}

@keyframes badge-entrance {
  0% { 
    transform: scale(0.5) translateY(-50px) rotate(-180deg);
    opacity: 0;
  }
  70% { 
    transform: scale(1.1) translateY(10px) rotate(10deg);
    opacity: 0.9;
  }
  100% { 
    transform: scale(1) translateY(0) rotate(0deg);
    opacity: 1;
  }
}

@keyframes badge-float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  33% { transform: translateY(-5px) rotate(1deg); }
  66% { transform: translateY(5px) rotate(-1deg); }
}

@keyframes badge-sparkle {
  0%, 100% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 2px 10px rgba(255, 255, 255, 0.8); }
  50% { box-shadow: 0 8px 32px rgba(249, 71, 136, 0.3), inset 0 2px 10px rgba(255, 255, 255, 1); }
}

@keyframes trophy-dance {
  0% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.05) rotate(5deg); }
  50% { transform: scale(1.1) rotate(0deg); }
  75% { transform: scale(1.05) rotate(-5deg); }
  100% { transform: scale(1) rotate(0deg); }
}

@keyframes special-sparkle {
  0%, 100% { filter: brightness(1) hue-rotate(0deg); }
  50% { filter: brightness(1.2) hue-rotate(15deg); }
}

@keyframes text-slide-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes text-celebration {
  0% { 
    transform: scale(1);
    color: #8B4513;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }
  100% { 
    transform: scale(1.02);
    color: #654321;
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
  }
}

@keyframes button-entrance {
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes button-celebration {
  0% { 
    transform: scale(1);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.5);
  }
  100% { 
    transform: scale(1.02);
    box-shadow: 0 8px 25px rgba(255, 215, 0, 0.7);
  }
}

@keyframes shine-sweep {
  0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
  100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
}

@keyframes glow-pulse {
  0%, 100% { 
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% { 
    opacity: 0.9;
    transform: translate(-50%, -50%) scale(1.1);
  }
}

@keyframes special-glow-pulse {
  0%, 100% { 
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(1);
  }
  50% { 
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes particle-float {
  0% {
    opacity: 0;
    transform: translateY(100vh) scale(0);
  }
  10% {
    opacity: 1;
    transform: translateY(90vh) scale(1);
  }
  90% {
    opacity: 1;
    transform: translateY(-10vh) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-20vh) scale(0);
  }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

/* Particules en arrière-plan - Plus nombreuses et plus dynamiques */
.particles-container {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 3px !important;
  height: 3px !important;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0) 70%
  ) !important;
  border-radius: 50%;
  animation: floatDust infinite;
  box-shadow: 0 0 6px 2px rgba(111, 168, 220, 0.2);
}

.particle:nth-child(3n) {
  background: radial-gradient(
    circle,
    rgba(168, 111, 220, 0.9) 0%,
    rgba(168, 111, 220, 0) 70%
  ) !important;
  box-shadow: 0 0 8px 2px rgba(168, 111, 220, 0.3);
}

.particle:nth-child(3n + 1) {
  background: radial-gradient(
    circle,
    rgba(111, 168, 220, 0.9) 0%,
    rgba(111, 168, 220, 0) 70%
  ) !important;
  box-shadow: 0 0 8px 2px rgba(111, 168, 220, 0.3);
}

@keyframes float {
  0% {
    transform: translateY(0) translateX(0) rotate(0deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
    transform: translateY(-50px) translateX(25px) rotate(180deg);
  }
  100% {
    transform: translateY(-100px) translateX(50px) rotate(360deg);
    opacity: 0;
  }
}

@keyframes particleExpand {
  0% {
    transform: translate(0, 0) scale(0);
    opacity: 0;
  }
  20% {
    opacity: 0.8;
  }
  100% {
    transform: translate(
        calc(cos(var(--angle)) * var(--distance)),
        calc(sin(var(--angle)) * var(--distance))
      )
      scale(0);
    opacity: 0;
  }
}

.current-level-badge {
  position: relative;
  margin-top: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.current-badge-image {
  width: 160px;
  height: 160px;
  object-fit: contain;
  transition: all 0.3s ease;
  cursor: pointer;
}

.current-badge-image:hover {
  transform: scale(1.1);
}

/* Conserver les styles existants pour le tooltip */
.badge-tooltip {
  position: fixed;
  background: rgba(30, 30, 45, 0.95);
  border-radius: 12px;
  padding: 15px;
  max-width: 250px;
  z-index: 1000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  pointer-events: none;
}

.badge-tooltip:after {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid rgba(30, 30, 45, 0.95);
}

.tooltip-title {
  font-weight: bold;
  font-size: 16px;
  color: white;
  margin-bottom: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.tooltip-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 10px;
  line-height: 1.4;
}

.tooltip-requirement {
  font-size: 12px;
  color: #4fc3f7;
  font-style: italic;
}

.badge-evolution-text {
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.9), rgba(255, 165, 0, 0.8));
  color: #8B4513;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.9rem;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
  border: 2px solid rgba(255, 215, 0, 0.6);
  animation: evolution-text-pulse 1.5s ease-in-out infinite;
}

.evolution-instruction {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: bold;
}

.click-icon {
  font-size: 1.2rem;
  animation: bounce-icon 1s ease-in-out infinite;
}

.evolution-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar-evolution {
  width: 80px;
  height: 6px;
  background-color: rgba(139, 69, 19, 0.3);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #FFD700, #FF6347);
  border-radius: 3px;
  transition: width 0.3s ease;
  box-shadow: 0 0 8px rgba(255, 215, 0, 0.6);
}

.progress-text {
  font-size: 0.8rem;
  font-weight: bold;
  color: #8B4513;
}

@keyframes evolution-text-pulse {
  0%, 100% {
    transform: translateX(-50%) scale(1);
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
  }
  50% {
    transform: translateX(-50%) scale(1.05);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
  }
}

@keyframes bounce-icon {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

/* Conteneur du badge avec effet d'évolution */
.badge-evolution-glow {
  position: relative;
  animation: evolution-ready-pulse 2s ease-in-out infinite;
}

.evolution-glow-ring {
  position: absolute;
  top: 62%;
  left: 50%;
  width: 120px;
  height: 120px;
  transform: translate(-50%, -50%);
  border: 3px solid #FFD700;
  border-radius: 50%;
  animation: glow-ring-pulse 1.5s ease-in-out infinite;
  z-index: -1;
}

.evolution-particles {
  position: absolute;
  top: 25%;
  left: 50%;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.evolution-particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #FFD700;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform-origin: 0 60px;
  animation: orbit-particle 3s linear infinite;
}

.click-progress-ring {
  position: absolute;
  top: 150%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  pointer-events: none;
}

.progress-circle {
  transform-origin: center;
  transform: rotate(-90deg);
  transition: stroke-dashoffset 0.3s ease;
  filter: drop-shadow(0 0 5px #FFD700);
}

/* Animations de clic améliorées */
.badge-click-animation {
  animation: enhanced-badge-click 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes enhanced-badge-click {
  0% {
    transform: scale(1) rotate(0deg);
    filter: brightness(1) hue-rotate(0deg);
  }
  25% {
    transform: scale(0.85) rotate(-5deg);
    filter: brightness(1.3) hue-rotate(30deg);
  }
  75% {
    transform: scale(1.1) rotate(5deg);
    filter: brightness(1.5) hue-rotate(-30deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    filter: brightness(1) hue-rotate(0deg);
  }
}

/* Animation d'évolution mega spectaculaire */
.badge-mega-evolve {
  animation: mega-evolution 2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.evolution-explosion {
  animation: explosion-container 2s ease-out;
}

@keyframes mega-evolution {
  0% {
    transform: scale(1) rotate(0deg);
    filter: brightness(1) contrast(1) saturate(1);
  }
  10% {
    transform: scale(1.2) rotate(10deg);
    filter: brightness(2) contrast(1.5) saturate(2);
  }
  20% {
    transform: scale(0.8) rotate(-10deg);
    filter: brightness(0.5) contrast(2) saturate(0.5);
  }
  30% {
    transform: scale(1.5) rotate(20deg);
    filter: brightness(3) contrast(2) saturate(3) hue-rotate(90deg);
  }
  50% {
    transform: scale(2) rotate(180deg);
    filter: brightness(4) contrast(3) saturate(4) hue-rotate(180deg);
  }
  70% {
    transform: scale(1.8) rotate(350deg);
    filter: brightness(3) contrast(2) saturate(3) hue-rotate(270deg);
  }
  85% {
    transform: scale(1.2) rotate(360deg);
    filter: brightness(2) contrast(1.5) saturate(2) hue-rotate(360deg);
  }
  100% {
    transform: scale(1) rotate(360deg);
    filter: brightness(1) contrast(1) saturate(1) hue-rotate(0deg);
  }
}

@keyframes explosion-container {
  0% { transform: scale(1); }
  20% { transform: scale(1.1); }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); }
}

@keyframes explosion-burst {
  0% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0) scale(1);
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(-1 * var(--distance))) scale(0);
    opacity: 0;
  }
}

@keyframes evolution-ready-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes glow-ring-pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 1;
  }
}

@keyframes orbit-particle {
  0% {
    transform: rotate(0deg);
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: rotate(360deg);
    opacity: 1;
  }
}

/* Bouton pour accéder au parcours */
.parcours-button-container {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 30;
}

.parcours-button {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(30, 30, 45, 0.7);
  padding: 10px 20px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-weight: bold;
  cursor: pointer;
  backdrop-filter: blur(5px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.parcours-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.4);
  background: rgba(40, 40, 60, 0.8);
}

.parcours-button-icon {
  font-size: 20px;
}

/* Avatar and Progress Ring - Highly enhanced! */
.avatar-container {
  position: relative;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.5s ease;
  z-index: 5;
}

.avatar-pulse {
  animation: avatarEnhancedPulse 1s ease;
}

@keyframes avatarEnhancedPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.progress-ring-container {
  position: absolute;
  width: 260px;
  height: 260px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: visible;
}

.progress-ring {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
  overflow: visible;
}

.progress-ring-circle {
  transition: stroke-dashoffset 1s ease-in-out;
  transform-origin: 50% 50%;
  stroke-dasharray: 754;
}

.progress-ring-circle-bg {
  stroke-dasharray: 754;
  stroke-dashoffset: 0;
}

.progress-ring-blur {
  stroke-dasharray: 754;
  stroke-dashoffset: 0;
  filter: blur(12px);
}

.avatar-image-container {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 2;
  transition: all 0.5s ease;
}

.avatar-image-container:hover {
  transform: scale(1.05);
}

.avatar-effects {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.avatar-effect-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  opacity: 0;
  animation: circleExpand 3s infinite;
}

.avatar-effect-circle:nth-child(1) {
  animation-delay: 0s;
}

.avatar-effect-circle:nth-child(2) {
  animation-delay: 1s;
}

.avatar-effect-circle:nth-child(3) {
  animation-delay: 2s;
}

@keyframes circleExpand {
  0% {
    width: 40%;
    height: 40%;
    border-color: rgba(79, 195, 247, 0.5);
    opacity: 0.7;
  }
  100% {
    width: 150%;
    height: 150%;
    border-color: rgba(255, 64, 129, 0);
    opacity: 0;
  }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.5s ease;
}

.avatar-container:hover .avatar-image {
  transform: scale(1.05);
}

.level-badge {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(to right, #4fc3f7, #7c4dff);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  z-index: 3;
}

/* Effet de mise en évidence de l'avatar */
.avatar-highlight-effect {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 300px;
  z-index: 4;
  pointer-events: none;
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 240px;
  height: 240px;
  border-radius: 50%;
  border: 4px solid #76ff03;
  box-shadow: 0 0 20px rgba(118, 255, 3, 0.7);
  opacity: 0.7;
  animation: pulse-ring 2s ease-out infinite;
}

.hint-arrow {
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s ease infinite;
  text-align: center;
}

.arrow {
  display: block;
  font-size: 36px;
  color: #76ff03;
  text-shadow: 0 0 10px rgba(118, 255, 3, 0.7);
}

.hint-text {
  display: block;
  color: white;
  font-weight: bold;
  font-size: 18px;
  margin-top: 5px;
  padding: 5px 10px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  white-space: nowrap;
}

@keyframes pulse-ring {
  0% {
    transform: translate(-50%, -50%) scale(0.9);
    opacity: 0.7;
  }
  50% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.9;
  }
  100% {
    transform: translate(-50%, -50%) scale(0.9);
    opacity: 0.7;
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateX(-50%) translateY(0);
  }
  40% {
    transform: translateX(-50%) translateY(-15px);
  }
  60% {
    transform: translateX(-50%) translateY(-7px);
  }
}

/* Styles pour améliorer l'intégration du guide */
.guide-avatar-container {
  z-index: 1050; /* S'assurer que le guide est au-dessus des autres éléments */
}

.guide-top-left {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 2000;
}

.speech-bubble {
  background-color: #fff;
  font-family: 'Comic Sans MS', 'Chalkboard SE', 'Marker Felt', sans-serif;
  color: #333;
  border: 2px solid #58cc02; /* Couleur verte de Duolingo */
}

.speech-bubble:after {
  border-color: #fff transparent transparent;
}

.bubble-header {
  border-bottom: 1px solid #58cc02;
}

.guide-name {
  color: #58cc02;
}

.option-button {
  background-color: #fff;
  border: 2px solid #58cc02;
  color: #58cc02;
  font-weight: bold;
  transition: all 0.2s ease;
}

.option-button:hover {
  background-color: #58cc02;
  color: #fff;
}

.avatar-container:hover .level-badge {
  transform: translateX(-50%) translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
  background: linear-gradient(to right, #7c4dff, #ff4081);
}

.avatar-glow {
  background: radial-gradient(
    circle,
    rgba(111, 168, 220, 0.5) 0%,
    rgba(168, 111, 220, 0.5) 50%,
    rgba(220, 111, 168, 0.5) 100%
  );
  filter: blur(20px);
  opacity: 0.7;
  animation: cosmicRotate 15s linear infinite;
}

.avatar-glow.pulse {
  animation: cosmicPulse 1s;
}

@keyframes avatarGlowPulse {
  0% {
    transform: scale(0.95);
    opacity: 0.5;
    filter: blur(15px);
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
    filter: blur(20px);
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
    filter: blur(15px);
  }
}

/* Menu d'interaction avec l'avatar */
.avatar-interaction {
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(30, 30, 30, 0.9);
  border-radius: 12px;
  padding: 15px;
  display: flex;
  gap: 25px;
  z-index: 10;
  animation: enhancedSlideUp 0.4s;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.3),
    0 0 15px rgba(79, 195, 247, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.animation-toggle {
  margin-left: 10px;
  background-color: rgba(30, 30, 45, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.animation-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
}

.animation-toggle.active {
  border-color: #4fc3f7;
  box-shadow: 0 0 15px rgba(79, 195, 247, 0.5);
}

.animation-toggle:not(.active) {
  opacity: 0.7;
}

.animation-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  color: #fff;
}

.animation-icon i {
  transition: all 0.3s ease;
}

.animation-toggle.active .animation-icon i {
  color: #4fc3f7;
}

.animation-toggle:not(.active) .animation-icon i {
  color: #aaa;
}

@keyframes enhancedSlideUp {
  from {
    transform: translateX(-50%) translateY(20px);
    opacity: 0;
    filter: blur(5px);
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
    filter: blur(0);
  }
}

.interaction-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.interaction-option:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.interaction-option svg {
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.interaction-option:hover svg {
  transform: scale(1.1);
  filter: drop-shadow(0 0 5px rgba(79, 195, 247, 0.5));
}

.interaction-option span {
  font-size: 14px;
  font-weight: 500;
}

/* Animation de récompense */
.achievement-popup {
  position: fixed;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(
    135deg,
    rgba(30, 30, 45, 0.9) 0%,
    rgba(30, 30, 60, 0.9) 100%
  );
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  z-index: 100;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.5),
    0 0 20px rgba(168, 111, 220, 0.4);
}

@keyframes enhancedDropDown {
  0% {
    transform: translateX(-50%) translateY(-50px);
    opacity: 0;
    filter: blur(10px);
  }
  70% {
    transform: translateX(-50%) translateY(10px);
  }
  100% {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
    filter: blur(0);
  }
}

@keyframes enhancedFadeOut {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
}

.achievement-icon {
  font-size: 40px;
  animation: trophyShine 2s infinite;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.7);
}

@keyframes trophyShine {
  0% {
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.7);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 215, 0, 1);
  }
  100% {
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.7);
  }
}

.achievement-text h3 {
  color: #a86fdc;
  text-shadow: 0 0 10px rgba(168, 111, 220, 0.7);
}

.achievement-text p {
  margin: 0;
  font-size: 16px;
  color: #ffffff;
}

/* Message d'avertissement pour les tutoriels */
.tutorial-warning-popup {
  position: fixed;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(
    135deg,
    rgba(45, 30, 15, 0.95) 0%,
    rgba(60, 40, 20, 0.95) 100%
  );
  border: 2px solid rgba(255, 165, 0, 0.5);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  z-index: 100;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.5),
    0 0 20px rgba(255, 165, 0, 0.3);
  animation: enhancedDropDown 0.6s ease-out;
  max-width: 400px;
}

.warning-icon {
  font-size: 40px;
  animation: warningPulse 1.5s infinite;
  text-shadow: 0 0 10px rgba(255, 165, 0, 0.7);
}

@keyframes warningPulse {
  0% {
    text-shadow: 0 0 10px rgba(255, 165, 0, 0.7);
    transform: scale(1);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 165, 0, 1);
    transform: scale(1.1);
  }
  100% {
    text-shadow: 0 0 10px rgba(255, 165, 0, 0.7);
    transform: scale(1);
  }
}

.warning-text h3 {
  color: #ffa500;
  text-shadow: 0 0 10px rgba(255, 165, 0, 0.5);
  margin: 0 0 8px 0;
}

.warning-text p {
  margin: 0;
  font-size: 16px;
  color: #ffffff;
  line-height: 1.4;
}

/* Styles pour le bouton Commencer à jouer */
.play-button-container {
  position: absolute;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30; /* Plus élevé que les autres éléments */
  pointer-events: auto; /* Le conteneur lui-même ne capture pas les événements */
}

.play-button {
  position: relative;
  padding: 16px 40px;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: white;
  background: linear-gradient(135deg, #f94788 0%, #6495f8 50%, #b152c7 100%);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transform: perspective(1px) translateZ(0);
  width: 320px;
  height: 60px;
  pointer-events: auto; /* Le bouton lui-même capture les événements */
  z-index: 100;
}

.play-button.disabled {
  background: linear-gradient(135deg, #666 0%, #888 50%, #666 100%);
  cursor: not-allowed;
  opacity: 0.6;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  transform: perspective(1px) translateZ(0) scale(0.98);
}

.play-button.disabled:hover {
  transform: perspective(1px) translateZ(0) scale(0.98);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.play-button.disabled .button-glow {
  display: none;
}

.button-text {
  position: relative;
  z-index: 3;
  transition: all 0.3s ease;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.button-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 30px;
  z-index: 1;
  transition: all 0.3s ease;
  opacity: 0;
  overflow: hidden;
}

.button-border:before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #f94788 0%, #6495f8 50%, #b152c7 100%);
  background-size: 200% 200%;
  border-radius: 32px;
  z-index: 1;
  opacity: 0.6;
}

.button-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: radial-gradient(
    circle at center,
    rgba(249, 71, 136, 0.8) 0%,
    rgba(100, 149, 248, 0.5) 50%,
    rgba(177, 82, 199, 0.8) 100%
  );
  filter: blur(15px);
  opacity: 0;
  transition: opacity 0.5s ease;
  z-index: 0;
  transform: translateZ(-1px);
}

.play-button:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(249, 71, 136, 0.8) 0%,
    rgba(100, 149, 248, 0.8) 50%,
    rgba(177, 82, 199, 0.8) 100%
  );
  border-radius: 30px;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.play-button:after {
  content: '';
  position: absolute;
  left: -50%;
  top: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0) 60%
  );
  opacity: 0;
  transform: scale(0);
  transition:
    transform 0.6s ease,
    opacity 0.6s ease;
  z-index: 2;
  pointer-events: none;
}

.play-button.hovered {
  transform: translateY(-5px) scale(1.03);
  box-shadow:
    0 15px 30px rgba(0, 0, 0, 0.3),
    0 0 15px rgba(249, 71, 136, 0.3),
    0 0 15px rgba(100, 149, 248, 0.3),
    0 0 15px rgba(177, 82, 199, 0.3);
}

.play-button.hovered .button-glow {
  opacity: 0.8;
}

.play-button.hovered .button-text {
  transform: scale(1.05);
}

.play-button.hovered .button-border {
  opacity: 1;
}

.play-button.hovered .button-border:before {
  animation: gradientShift 3s ease infinite;
}

.play-button.pressed {
  transform: translateY(-2px) scale(0.98);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.play-button.pressed:after {
  opacity: 0.5;
  transform: scale(1);
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Adaptations pour les écrans plus petits */

/* Très grands écrans (> 1920px) */
@media (min-width: 1921px) {
  .avatar-container {
    width: 280px;
    height: 280px;
  }

  .progress-ring-container {
    width: 320px;
    height: 320px;
  }

  .progress-ring {
    width: 320px;
    height: 320px;
  }

  .avatar-image-container {
    width: 240px;
    height: 240px;
  }

  .current-badge-image {
    width: 180px;
    height: 180px;
  }

  .play-button {
    font-size: 1.3rem;
    padding: 18px 42px;
  }
}

/* Écrans moyens (1024px - 1440px) */
@media (max-width: 1440px) {
  .dashboard-container {
    padding-top: 12vh;
  }

  .avatar-container {
    width: 220px;
    height: 220px;
  }

  .progress-ring-container {
    width: 250px;
    height: 250px;
  }

  .progress-ring {
    width: 250px;
    height: 250px;
  }

  .avatar-image-container {
    width: 180px;
    height: 180px;
  }

  .current-badge-image {
    width: 140px;
    height: 140px;
  }

  .play-button {
    font-size: 1rem;
    padding: 14px 32px;
  }
}

/* Tablettes et petits écrans (768px - 1024px) */
@media (max-width: 1024px) {
  .dashboard-container {
    padding-top: 10vh;
  }

  .avatar-container {
    width: 200px;
    height: 200px;
  }

  .progress-ring-container {
    width: 230px;
    height: 230px;
  }

  .progress-ring {
    width: 230px;
    height: 230px;
  }

  .avatar-image-container {
    width: 160px;
    height: 160px;
  }

  .current-badge-image {
    width: 120px;
    height: 120px;
  }

  .play-button {
    font-size: 0.95rem;
    padding: 12px 28px;
  }

  .theme-selector {
    bottom: 15px;
    left: 70px;
    gap: 12px;
    padding: 8px 12px;
  }

  .theme-tab {
    width: 48px;
    height: 48px;
  }
}

/* Mobiles en mode paysage et petites tablettes (576px - 768px) */
@media (max-width: 768px) {
  .dashboard-container {
    padding-top: 8vh;
  }

  .avatar-container {
    width: 180px;
    height: 180px;
  }

  .progress-ring-container {
    width: 210px;
    height: 210px;
  }

  .progress-ring {
    width: 210px;
    height: 210px;
  }

  .avatar-image-container {
    width: 140px;
    height: 140px;
  }

  .level-badge {
    font-size: 0.85rem;
    padding: 4px 10px;
  }

  .current-badge-image {
    width: 100px;
    height: 100px;
  }

  .badge-evolution-text {
    font-size: 0.85rem;
  }

  .play-button {
    font-size: 0.9rem;
    padding: 12px 24px;
    width: auto;
    height: auto;
  }

  .play-button-container {
    bottom: 70px;
  }

  .theme-selector {
    bottom: 10px;
    left: 60px;
    gap: 8px;
    padding: 6px 10px;
    flex-wrap: wrap;
    max-width: calc(100vw - 80px);
  }

  .theme-option {
    padding: 5px;
  }

  .theme-icon {
    width: 24px;
    height: 24px;
  }

  .theme-option span {
    font-size: 10px;
  }

  .theme-tab {
    width: 44px;
    height: 44px;
    bottom: 10px;
    left: 10px;
  }
}

/* Petits mobiles (< 576px) */
@media (max-width: 576px) {
  .dashboard-container {
    padding-top: 6vh;
  }

  .avatar-container {
    width: 160px;
    height: 160px;
  }

  .progress-ring-container {
    width: 190px;
    height: 190px;
  }

  .progress-ring {
    width: 190px;
    height: 190px;
  }

  .avatar-image-container {
    width: 120px;
    height: 120px;
  }

  .level-badge {
    font-size: 0.75rem;
    padding: 3px 8px;
    bottom: 5px;
  }

  .current-badge-image {
    width: 80px;
    height: 80px;
  }

  .badge-evolution-text {
    font-size: 0.75rem;
    padding: 8px;
  }

  .evolution-instruction {
    font-size: 0.7rem;
  }

  .progress-text {
    font-size: 0.7rem;
  }

  .play-button {
    font-size: 0.85rem;
    padding: 10px 20px;
    width: auto;
    height: auto;
  }

  .play-button-container {
    bottom: 50px;
  }

  .theme-selector {
    bottom: 5px;
    left: 50px;
    gap: 6px;
    padding: 5px 8px;
    font-size: 9px;
  }

  .theme-option {
    padding: 4px;
  }

  .theme-icon {
    width: 20px;
    height: 20px;
  }

  .theme-option span {
    font-size: 8px;
  }

  .theme-tab {
    width: 40px;
    height: 40px;
    bottom: 5px;
    left: 5px;
  }

  .theme-tab-icon {
    width: 20px;
    height: 20px;
  }

  /* Adapter les animations de badge */
  .badge-unlock-overlay {
    padding: 10px;
  }

  .badge-container {
    width: 100px;
    height: 100px;
  }

  .badge-icon {
    font-size: 3rem;
  }

  .text-container h2 {
    font-size: 1.2rem;
  }

  .text-container h3 {
    font-size: 1rem;
  }

  .text-container p {
    font-size: 0.85rem;
  }
}

/* Très petits écrans (< 375px) */
@media (max-width: 375px) {
  .dashboard-container {
    padding-top: 5vh;
  }

  .avatar-container {
    width: 140px;
    height: 140px;
  }

  .progress-ring-container {
    width: 170px;
    height: 170px;
  }

  .progress-ring {
    width: 170px;
    height: 170px;
  }

  .avatar-image-container {
    width: 100px;
    height: 100px;
  }

  .level-badge {
    font-size: 0.7rem;
    padding: 2px 6px;
  }

  .current-badge-image {
    width: 70px;
    height: 70px;
  }

  .play-button {
    font-size: 0.8rem;
    padding: 8px 16px;
    width: auto;
    height: auto;
  }

  .play-button-container {
    bottom: 40px;
  }
}

/* Accessibility overrides for large/circular elements */
.avatar-container.highlight-clickable {
  outline-offset: 15px !important;
}

.theme-tab.highlight-clickable {
  outline-offset: 5px !important;
}

.current-badge-image.highlight-clickable {
  outline-offset: 8px !important;
}
</style>